/**
 * Integrytas — Refresh Demo Database
 * Page: DDM.REFRESH
 *
 * Job history for the demo-schema refresh (ddm module — see
 * supabase/migrations/20260719000000_ddm_module.sql). Any role granted this
 * page (RBAC, §6 — not hardcoded to admin) can trigger a refresh; a pg_cron
 * worker (ddm_process_pending_jobs) picks up the row this page inserts and
 * does the actual public -> demo clone + copy.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { executeCommonRoutine } from '../../lib/common_routine';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { useFormatDateTime } from '../../lib/format_date';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw } from 'lucide-react';

const PAGE_CODE = 'DDM.REFRESH';

type StatusCode = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
const STATUS_VARIANT: Record<StatusCode, StatusVariant> = {
  PENDING: 'neutral', RUNNING: 'info', SUCCEEDED: 'success', FAILED: 'danger',
};
const STATUS_VALUES: StatusCode[] = ['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED'];
const ACTIVE_STATUSES = new Set<StatusCode>(['PENDING', 'RUNNING']);

interface DdmRefreshRow {
  ddm_refresh_job_id: number;
  status_code: StatusCode;
  requested_by: number;
  requester: { display_name: string; email: string } | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  row_count: number | null;
  error_message: string | null;
}

export interface DdmRefreshPageProps { session: AuthSession; }

export function DdmRefreshPage({ session }: DdmRefreshPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <DdmRefreshPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DdmRefreshPageInner({ session }: DdmRefreshPageProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<DdmRefreshRow[]>([]);
  const [statusFacet, setStatusFacet] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // §17.B13: related-children expand/contract. A refresh job is a log row with no
  // child collections to expand, so the disclosure renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const statusLabel = (s: StatusCode) => t(`ddm.status.${s}`, s.charAt(0) + s.slice(1).toLowerCase());

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('ddm_refresh_job')
        .select('ddm_refresh_job_id, status_code, requested_by, requester:csm_user_master!fk_ddm_refresh_job_requested_by(display_name, email), created_at, started_at, completed_at, row_count, error_message')
        .order('created_at', { ascending: false })
        .limit(50);
      if (e) throw e;
      setRows((data ?? []) as unknown as DdmRefreshRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Poll while a job is in flight, so the row's status updates without the
  // user having to click Reload themselves.
  useEffect(() => {
    const hasActive = rows.some(r => ACTIVE_STATUSES.has(r.status_code));
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => { void reload(); }, 3000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [rows]);

  const filtered = useMemo(
    () => rows.filter(r => statusFacet.length === 0 || statusFacet.includes(r.status_code)),
    [rows, statusFacet],
  );

  const triggerRefresh = async () => {
    setTriggering(true); setError(null);
    try {
      const actor = getForensicActorId(session);
      const result = await executeCommonRoutine({
        table_name: 'ddm_refresh_job',
        operation: 'ADD',
        row: { requested_by: actor },
        actor_user_id: actor,
      });
      if (result.result_code !== 'OK') {
        throw new Error(result.errors[0]?.message ?? 'CSM.COMMON.REJECTED');
      }
      setConfirming(false);
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setTriggering(false); }
  };

  const GRID_COLS: GridColumnDef[] = [
    { key: 'requester',     header: t('ddm.col_requested_by', 'Requested By'), defaultWidth: 200 },
    { key: 'status_code',   header: t('common.status', 'Status'),             defaultWidth: 120 },
    { key: 'created_at',    header: t('ddm.col_requested_at', 'Requested At'), defaultWidth: 180 },
    { key: 'started_at',    header: t('ddm.col_started_at', 'Started At'),     defaultWidth: 180 },
    { key: 'completed_at',  header: t('ddm.col_completed_at', 'Completed At'), defaultWidth: 180 },
    { key: 'row_count',     header: t('ddm.col_row_count', 'Rows Copied'),     defaultWidth: 110 },
    { key: 'error_message', header: t('ddm.col_error', 'Error'),               defaultWidth: 260 },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('ddm.page_title', 'Refresh Demo Database')}
          description={t('ddm.list.description', 'Trigger a demo-schema refresh and track the history of refresh jobs.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}
              </button>
              <button type="button" onClick={() => setConfirming(true)} className="btn-primary flex items-center gap-1.5">
                {t('ddm.trigger_button', 'Refresh Demo Database')}
              </button>
            </>
          }
        />

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <FacetFilter
              label={t('common.status', 'Status')}
              options={STATUS_VALUES.map(v => ({ value: v, label: statusLabel(v) }))}
              selected={statusFacet}
              onChange={setStatusFacet}
            />
            <ClearFiltersButton active={statusFacet.length > 0} onClear={() => setStatusFacet([])} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('ddm.col_related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.ddm_refresh_job_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.ddm_refresh_job_id)}
                        hasChildren={hasRelated(r.ddm_refresh_job_id)}
                        onToggle={() => detail.toggle(r.ddm_refresh_job_id)}
                        expandLabel={t('ddm.related.expand', 'Show related records')}
                        collapseLabel={t('ddm.related.collapse', 'Hide related')}
                        tooltip={t('ddm.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'requester' && (r.requester?.display_name || r.requester?.email || '—')}
                        {col.key === 'status_code' && <StatusBadge label={statusLabel(r.status_code)} variant={STATUS_VARIANT[r.status_code]} />}
                        {col.key === 'created_at' && formatDateTime(r.created_at)}
                        {col.key === 'started_at' && (r.started_at ? formatDateTime(r.started_at) : '—')}
                        {col.key === 'completed_at' && (r.completed_at ? formatDateTime(r.completed_at) : '—')}
                        {col.key === 'row_count' && (r.row_count ?? '—')}
                        {col.key === 'error_message' && (r.error_message ?? '—')}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.ddm_refresh_job_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.ddm_refresh_job_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center">
                    <EmptyState
                      title={t('ddm.no_jobs', 'No refresh jobs yet')}
                      action={<button onClick={() => setConfirming(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('ddm.trigger_button', 'Refresh Demo Database')}</button>}
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {confirming && (
        <Modal
          title={t('ddm.confirm_title', 'Refresh Demo Database?')}
          onClose={() => setConfirming(false)}
          width="sm"
          footer={
            <>
              <button onClick={() => setConfirming(false)} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={() => void triggerRefresh()} disabled={triggering}
                className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
                {triggering ? t('ddm.triggering', 'Starting…') : t('ddm.confirm_button', 'Refresh')}
              </button>
            </>
          }
        >
          <p className="text-csm-sm text-csm-ink-soft">
            {t('ddm.confirm_body', 'This replaces all data in the demo schema with a fresh copy of production. It does not change or affect production data.')}
          </p>
        </Modal>
      )}
    </div>
  );
}
